import { create } from 'zustand';
import { PERSIST_KEY } from 'utils/environment';
import type { Entity } from 'containers/Entities/entities.types';
import type { Organization } from 'containers/Organizations/organizations.types';


export type LoginError = {
  error: boolean;
  message: string;
};

export type SelectedEntityId = Entity['entityId'];
export type SelectedOrganizationId = Organization['organizationId'];

type TenantScope = {
  authtoken: string;
  selectedEntityId: SelectedEntityId;
  selectedOrganizationId: SelectedOrganizationId;
};

export function maybeGetUrlToken(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('token');
}

export function loadPersistedAuth(): {
  authenticated: boolean;
  authtoken: string;
} {
  const urlToken = maybeGetUrlToken();
  if (urlToken) return { authenticated: true, authtoken: urlToken };
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(PERSIST_KEY);
      if (raw) {
        const persisted = JSON.parse(raw)?.global ?? {};
        const authtoken: string = persisted.authtoken ?? '';
        return { authenticated: Boolean(authtoken), authtoken };
      }
    }
  } catch {
    /* corrupt blob — fall through to logged-out */
  }
  return { authenticated: false, authtoken: '' };
}

const persistedAuth = loadPersistedAuth();

const INITIAL_LOGIN_ERROR: LoginError = { error: false, message: '' };

interface AuthStoreState {
  authenticated: boolean;
  authtoken: string;
  tenantSwitchPending: boolean;
  tenantScopeRevision: number;
  loginError: LoginError;
  selectedEntityId: Entity['entityId'];
  selectedOrganizationId: Organization['organizationId'];
  urlParamsOnPageLoad: string;
  setAuthToken: (authtoken: string) => void;
  setTenantSwitchPending: (tenantSwitchPending: boolean) => void;
  commitTenantScope: (tenantScope: TenantScope) => void;
  setLoginError: (loginError: LoginError) => void;
  selectEntity: (selectedEntityId: number) => void;
  setSelectedEntityId: (selectedEntityId: number) => void;
  setSelectedOrganizationId: (selectedOrganizationId: number) => void;
  setUrlParamsOnPageLoad: (urlParamsOnPageLoad: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthStoreState>((set) => ({
  authenticated: persistedAuth.authenticated,
  authtoken: persistedAuth.authtoken,
  tenantSwitchPending: false,
  tenantScopeRevision: 0,
  loginError: INITIAL_LOGIN_ERROR,
  selectedEntityId: NaN,
  selectedOrganizationId: NaN,
  urlParamsOnPageLoad:
    typeof window !== 'undefined' ? window.location.search : '',
  setAuthToken: (authtoken) =>
    set({ authenticated: Boolean(authtoken), authtoken }),
  setTenantSwitchPending: (tenantSwitchPending) =>
    set({ tenantSwitchPending }),
  commitTenantScope: (tenantScope) =>
    set((state) => ({
      ...tenantScope,
      authenticated: Boolean(tenantScope.authtoken),
      tenantSwitchPending: false,
      tenantScopeRevision: state.tenantScopeRevision + 1,
    })),
  setLoginError: (loginError) => set({ loginError }),
  selectEntity: (selectedEntityId) =>
    set({ selectedEntityId, selectedOrganizationId: NaN }),
  setSelectedEntityId: (selectedEntityId) => set({ selectedEntityId }),
  setSelectedOrganizationId: (selectedOrganizationId) =>
    set({ selectedOrganizationId }),
  setUrlParamsOnPageLoad: (urlParamsOnPageLoad) => set({ urlParamsOnPageLoad }),
  logout: () =>
    set({
      authenticated: false,
      authtoken: '',
      tenantSwitchPending: false,
      selectedEntityId: NaN,
      selectedOrganizationId: NaN,
      urlParamsOnPageLoad: '',
    }),
}));

if (typeof window !== 'undefined') {
  useAuthStore.subscribe((state, prev) => {
    if (
      state.authenticated === prev.authenticated &&
      state.authtoken === prev.authtoken
    ) {
      return;
    }
    try {
      localStorage.setItem(
        PERSIST_KEY,
        JSON.stringify({
          global: {
            authenticated: state.authenticated,
            authtoken: state.authtoken,
          },
        })
      );
    } catch {
      /* quota / disabled storage — non-fatal, session stays in memory */
    }
  });
}

export const setAuthToken = (authtoken: string) =>
  useAuthStore.getState().setAuthToken(authtoken);
export const setTenantSwitchPending = (pending: boolean) =>
  useAuthStore.getState().setTenantSwitchPending(pending);
export const commitTenantScope = (tenantScope: TenantScope) =>
  useAuthStore.getState().commitTenantScope(tenantScope);
export const setLoginError = (loginError: LoginError) =>
  useAuthStore.getState().setLoginError(loginError);
export const selectEntity = (id: number) =>
  useAuthStore.getState().selectEntity(id);
export const setSelectedEntityId = (id: number) =>
  useAuthStore.getState().setSelectedEntityId(id);
export const setSelectedOrganizationId = (id: number) =>
  useAuthStore.getState().setSelectedOrganizationId(id);
export const setUrlParamsOnPageLoad = (params: string) =>
  useAuthStore.getState().setUrlParamsOnPageLoad(params);
export const logout = () => useAuthStore.getState().logout();

export const getAuthtoken = () => useAuthStore.getState().authtoken;
export const getAuthenticated = () => useAuthStore.getState().authenticated;
export const getSelectedEntityId = () =>
  useAuthStore.getState().selectedEntityId;
export const getSelectedOrganizationId = () =>
  useAuthStore.getState().selectedOrganizationId;
export const getUrlParamsOnPageLoad = () =>
  useAuthStore.getState().urlParamsOnPageLoad;

export const useAuthenticated = () => useAuthStore((s) => s.authenticated);
export const useAuthtoken = () => useAuthStore((s) => s.authtoken);
export const useTenantSwitchPending = () =>
  useAuthStore((s) => s.tenantSwitchPending);
export const useTenantScopeRevision = () =>
  useAuthStore((s) => s.tenantScopeRevision);
export const useLoginError = () => useAuthStore((s) => s.loginError);
export const useSelectedEntityId = () =>
  useAuthStore((s) => s.selectedEntityId);
export const useSelectedOrganizationId = () =>
  useAuthStore((s) => s.selectedOrganizationId);
export const useUrlParamsOnPageLoad = () =>
  useAuthStore((s) => s.urlParamsOnPageLoad);
